import React, { createContext, useCallback, useContext, useEffect, useState } from 'react';

export type Tier = 'anon' | 'free' | 'pro';
export interface AuthUser { email: string; name: string | null; isAdmin?: boolean; }

interface AuthState {
  loading: boolean;
  user: AuthUser | null;
  tier: Tier;
  googleEnabled: boolean;
  refresh: () => Promise<void>;
  logout: () => Promise<void>;
}

const AuthContext = createContext<AuthState>({
  loading: true, user: null, tier: 'anon', googleEnabled: false,
  refresh: async () => {}, logout: async () => {},
});

function readCookie(name: string): string {
  const m = document.cookie.match(new RegExp(`(?:^|; )${name}=([^;]*)`));
  return m ? decodeURIComponent(m[1]) : '';
}

/** fetch with the session cookie and CSRF header, for code not using the axios client. */
export function authFetch(url: string, init: RequestInit = {}): Promise<Response> {
  const headers = new Headers(init.headers);
  if (init.body && !headers.has('Content-Type')) headers.set('Content-Type', 'application/json');
  const csrf = readCookie('fmr8_csrf');
  if (csrf) headers.set('x-csrf-token', csrf);
  return fetch(url, { ...init, headers, credentials: 'same-origin' });
}

export const AuthProvider: React.FC<{ children: React.ReactNode }> = ({ children }) => {
  const [loading, setLoading] = useState(true);
  const [user, setUser] = useState<AuthUser | null>(null);
  const [tier, setTier] = useState<Tier>('anon');
  const [googleEnabled, setGoogleEnabled] = useState(false);

  const refresh = useCallback(async () => {
    try {
      const r = await authFetch('/api/auth/me');
      const d = await r.json();
      setUser(d.user || null);
      setTier(d.user ? d.tier : 'anon');
      setGoogleEnabled(!!d.googleEnabled);
    } catch {
      setUser(null);
      setTier('anon');
    } finally {
      setLoading(false);
    }
  }, []);

  const logout = useCallback(async () => {
    await authFetch('/api/auth/logout', { method: 'POST' }).catch(() => {});
    setUser(null);
    setTier('anon');
  }, []);

  useEffect(() => { refresh(); }, [refresh]);

  return (
    <AuthContext.Provider value={{ loading, user, tier, googleEnabled, refresh, logout }}>
      {children}
    </AuthContext.Provider>
  );
};

export const useAuth = () => useContext(AuthContext);
export const hasTier = (tier: Tier, needed: 'free' | 'pro') =>
  needed === 'free' ? tier !== 'anon' : tier === 'pro';
